import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SectionDecor } from "@/components/SectionDecor";
import tileToShingle from "@/assets/specialty/tile-to-shingle-conversion.jpg";
import pictureFrameSolar from "@/assets/specialty/picture-frame-solar.jpg";

type Item = {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  img: string;
  alt: string;
  href: string;
  cta: string;
};

const items: Item[] = [
  {
    eyebrow: "Specialty work",
    title: "Tile removal to comp shingle conversions",
    body: "Plenty of Southern California homes were built with heavy concrete tile that has outlived its underlayment. Instead of resetting old tile over new paper, we strip the roof, check the sheathing and framing, and convert it to a modern architectural composition shingle system. The roof gets lighter, the details get simpler, and future repairs stop being a guessing game.",
    bullets: [
      "Full tear-off with sheathing and framing inspected before anything goes back on",
      "New underlayment, edge metal, valleys and ventilation as one system",
      "Lighter dead load than concrete tile and cleaner flashing details",
      "Debris hauled the same day, driveway and landscaping protected",
    ],
    img: tileToShingle,
    alt: "Southern California home mid tile-to-shingle conversion with new architectural shingles on one slope",
    href: "/services/roof-replacement",
    cta: "See roof replacement",
  },
  {
    eyebrow: "Specialty work",
    title: "Picture-frame solar installs",
    body: "A picture-frame layout keeps the array squared up and centered on the roof plane with an even border of roofing all the way around. It looks intentional from the street, it keeps our crews off the edges during service, and it leaves clean access for future repairs. We lay out the frame first, then set attachments into rafters and flash every penetration properly.",
    bullets: [
      "Symmetrical layout with even setbacks on every side of the array",
      "Attachments landed in rafters, each penetration flashed and sealed",
      "Conduit runs planned to stay out of sight from the street",
      "Roof condition reviewed first so panels are not going over a tired roof",
    ],
    img: pictureFrameSolar,
    alt: "Picture-frame solar array centered on a tile roof with even borders on all sides",
    href: "/services/solar-installation",
    cta: "See solar installation",
  },
];

export function SpecialtyWork() {
  return (
    <section className="relative overflow-hidden bg-background">
      <SectionDecor variant="roof" side="left" tone="primary" opacity={0.05} size={700} />
      <div className="relative z-10 mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">What we are known for</div>
          <h2 className="mt-3 font-serif text-4xl font-bold text-primary sm:text-5xl text-balance">
            The work homeowners call us for by name
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Two jobs come up more than anything else in our Southern California service area. Both are the kind of work
            that shows whether a crew actually knows roofing and solar together.
          </p>
        </div>

        <div className="mt-14 flex flex-col gap-16 sm:gap-20">
          {items.map((item, i) => (
            <div
              key={item.title}
              className={`grid items-center gap-8 lg:grid-cols-2 lg:gap-14 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            >
              <div className="overflow-hidden rounded-3xl border border-border shadow-elegant">
                <img
                  src={item.img}
                  alt={item.alt}
                  loading="lazy"
                  width={1600}
                  height={1104}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary">{item.eyebrow}</div>
                <h3 className="mt-3 font-serif text-3xl font-bold text-primary sm:text-4xl text-balance">{item.title}</h3>
                <p className="mt-4 leading-relaxed text-muted-foreground">{item.body}</p>
                <ul className="mt-5 space-y-2.5">
                  {item.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-foreground">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">Get Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link to={item.href}>{item.cta}</Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
